import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { createUserDevice } from '../api/users';
import { applyNativePushSwitchover } from '../api/usersNotificationsPreferences';

/**
 * Request push notification permission and register the device with the backend.
 * Sends { push_token, platform } to POST /users/:id/devices.
 *
 * When `applySwitchover` is true (i.e. the user just explicitly enabled push via the
 * opt-in banner), the native push switchover is applied server-side: SMS is disabled,
 * in-app + push are enabled, and (for volunteers) most email notifications are turned
 * off. This must NOT run on the silent startup re-registration (RES-4735).
 *
 * NOTE: The backend must handle 'push_token' + 'platform' fields (in addition to
 * the existing 'push_subscription' field used for web push) and route iOS tokens
 * to APNs and Android tokens to FCM.
 */
// Guard against duplicate listeners if registerNativePush is called more than once.
let _registrationListenersAdded = false;

export const registerNativePush = async (userId, { applySwitchover = false } = {}) => {
  if (!Capacitor.isNativePlatform()) return;

  const permission = await PushNotifications.requestPermissions();

  console.log('---> permission: ', permission);

  if (permission.receive !== 'granted') return;

  if (applySwitchover) {
    try {
      await applyNativePushSwitchover(userId);
    } catch (err) {
      console.error('Failed to apply native push notification switchover', err);
    }
  }

  console.log('---> _registrationListenersAdded: ', _registrationListenersAdded);

  if (!_registrationListenersAdded) {
    _registrationListenersAdded = true;

    await PushNotifications.addListener('registration', async (token) => {
      console.log('---> #1', userId, {
        user_agent: window.navigator.userAgent,
        push_token: token.value,
        platform: Capacitor.getPlatform(), // 'ios' or 'android'
      });

      try {
        await createUserDevice(userId, {
          user_agent: window.navigator.userAgent,
          push_token: token.value,
          platform: Capacitor.getPlatform(), // 'ios' or 'android'
        });
      } catch (err) {
        console.error('Failed to register push token with backend', err);
      }
    });

    await PushNotifications.addListener('registrationError', (err) => {
      console.error('Push registration error', err);
    });
  }

  await PushNotifications.register();
};

/**
 * Set up persistent listeners for incoming notifications and notification taps.
 * Call once at app startup (src/index.js) on native platforms.
 */
export const addPushNotificationListeners = () => {
  if (!Capacitor.isNativePlatform()) return;

  // Notification received while app is in the foreground — iOS shows a system banner automatically.
  PushNotifications.addListener('pushNotificationReceived', (notification) => {
    console.log('Push notification received (foreground)', notification);
  });

  // User tapped on a notification — handle deep linking via redirect_url in data payload.
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const redirectUrl = action.notification?.data?.redirect_url;
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  });
};
